import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { API_URL } from '../config';
import { getLearningPath, getCachedWorldMap as getFirebaseWorldMap, getUserData } from '../services/firebase';

const DataCacheContext = createContext();

export const useDataCache = () => {
    const context = useContext(DataCacheContext);
    if (!context) {
        throw new Error('useDataCache must be used within a DataCacheProvider');
    }
    return context;
};

// In-memory cache with TTL
const memoryCache = new Map();
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes (increased for better UX)

const getCacheKey = (type, ...args) => `${type}:${args.join(':')}`;

const getFromCache = (key) => {
    const cached = memoryCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.data;
    }
    if (cached) {
        memoryCache.delete(key); // Expired
    }
    return null;
};

const setCache = (key, data) => {
    memoryCache.set(key, { data, timestamp: Date.now() });
};

export const DataCacheProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [prefetchStatus, setPrefetchStatus] = useState({});
    const prefetchingRef = useRef(new Set());

    // Prefetch learning path data
    const prefetchLearningPath = useCallback(async (topic) => {
        if (!currentUser || !topic) return null;

        const cacheKey = getCacheKey('learningPath', currentUser.uid, topic);

        // Check memory cache first
        const cached = getFromCache(cacheKey);
        if (cached) return cached;

        // Prevent duplicate fetches
        if (prefetchingRef.current.has(cacheKey)) return null;
        prefetchingRef.current.add(cacheKey);

        try {
            const result = await getLearningPath(currentUser.uid, topic);
            if (result.success) {
                setCache(cacheKey, result.data);
                return result.data;
            }
        } catch (err) {
            console.log('Prefetch learning path error:', err.message);
        } finally {
            prefetchingRef.current.delete(cacheKey);
        }
        return null;
    }, [currentUser]);

    // Prefetch world map data
    const prefetchWorldMap = useCallback(async (topic) => {
        if (!currentUser || !topic) return null;

        const cacheKey = getCacheKey('worldMap', currentUser.uid, topic);

        const cached = getFromCache(cacheKey);
        if (cached) return cached;

        if (prefetchingRef.current.has(cacheKey)) return null;
        prefetchingRef.current.add(cacheKey);

        try {
            const result = await getFirebaseWorldMap(currentUser.uid, topic);
            if (result.success) {
                setCache(cacheKey, result.data);
                return result.data;
            }
        } catch (err) {
            console.log('Prefetch world map error:', err.message);
        } finally {
            prefetchingRef.current.delete(cacheKey);
        }
        return null;
    }, [currentUser]);

    // Prefetch boss battle questions
    const prefetchBossBattle = useCallback(async (topic) => {
        if (!topic) return null;

        const cacheKey = getCacheKey('bossBattle', topic);

        const cached = getFromCache(cacheKey);
        if (cached) return cached;

        if (prefetchingRef.current.has(cacheKey)) return null;
        prefetchingRef.current.add(cacheKey);

        try {
            const response = await fetch(`${API_URL}/api/generate-boss-battle`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ topic, questionCount: 10 })
            });
            const data = await response.json();
            if (data.questions) {
                setCache(cacheKey, data);
                return data;
            }
        } catch (err) {
            console.log('Prefetch boss battle error:', err.message);
        } finally {
            prefetchingRef.current.delete(cacheKey);
        }
        return null;
    }, []);

    // Prefetch game content
    const prefetchGameContent = useCallback(async (templateId, topic, moduleTitle, concepts, difficulty) => {
        const cacheKey = getCacheKey('gameContent', templateId, topic, moduleTitle);

        const cached = getFromCache(cacheKey);
        if (cached) return cached;

        if (prefetchingRef.current.has(cacheKey)) return null;
        prefetchingRef.current.add(cacheKey);

        try {
            const response = await fetch(`${API_URL}/api/generate-game-content`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ templateId, topic, moduleTitle, concepts, difficulty })
            });
            const data = await response.json();
            if (data.success || data.questions || data.tiles) {
                setCache(cacheKey, data);
                return data;
            }
        } catch (err) {
            console.log('Prefetch game content error:', err.message);
        } finally {
            prefetchingRef.current.delete(cacheKey);
        }
        return null;
    }, []);

    // Get cached data (returns immediately if cached, otherwise fetches)
    const getCachedLearningPath = useCallback(async (topic) => {
        const cacheKey = getCacheKey('learningPath', currentUser?.uid, topic);
        const cached = getFromCache(cacheKey);
        if (cached) return { success: true, data: cached, fromCache: true };

        // Not cached, fetch it
        const data = await prefetchLearningPath(topic);
        if (data) return { success: true, data, fromCache: false };
        return { success: false };
    }, [currentUser, prefetchLearningPath]);

    const getCachedWorldMap = useCallback(async (topic) => {
        const cacheKey = getCacheKey('worldMap', currentUser?.uid, topic);
        const cached = getFromCache(cacheKey);
        if (cached) return { success: true, data: cached, fromCache: true };

        const data = await prefetchWorldMap(topic);
        if (data) return { success: true, data, fromCache: false };
        return { success: false };
    }, [currentUser, prefetchWorldMap]);

    const getCachedBossBattle = useCallback(async (topic) => {
        const cacheKey = getCacheKey('bossBattle', topic);
        const cached = getFromCache(cacheKey);
        if (cached) return { ...cached, fromCache: true };

        const data = await prefetchBossBattle(topic);
        if (data) return { ...data, fromCache: false };
        return null;
    }, [prefetchBossBattle]);

    const getCachedGameContent = useCallback(async (templateId, topic, moduleTitle, concepts, difficulty) => {
        const cacheKey = getCacheKey('gameContent', templateId, topic, moduleTitle);
        const cached = getFromCache(cacheKey);
        if (cached) return { ...cached, fromCache: true };

        const data = await prefetchGameContent(templateId, topic, moduleTitle, concepts, difficulty);
        if (data) return { ...data, fromCache: false };
        return null;
    }, [prefetchGameContent]);

    // Prefetch all data for a topic in background
    const prefetchAllForTopic = useCallback(async (topic) => {
        if (!currentUser || !topic) return;

        console.log(`📦 Background prefetching data for: ${topic}`);

        // Fire all prefetches in parallel, don't await
        Promise.all([
            prefetchLearningPath(topic),
            prefetchWorldMap(topic),
            prefetchBossBattle(topic)
        ]).then(() => {
            console.log(`✅ Prefetch complete for: ${topic}`);
        }).catch(() => {
            // Ignore errors in background prefetch
        });
    }, [currentUser, prefetchLearningPath, prefetchWorldMap, prefetchBossBattle]);

    // Clear cache for a topic (useful after completing activities)
    const invalidateCache = useCallback((type, ...args) => {
        if (type === 'all') {
            memoryCache.clear();
        } else {
            const cacheKey = getCacheKey(type, ...args);
            memoryCache.delete(cacheKey);
        }
    }, []);

    // Prefetch user's active topics on login
    useEffect(() => {
        if (!currentUser) return;

        const prefetchUserTopics = async () => {
            try {
                const result = await getUserData(currentUser.uid);
                if (result.success && result.data?.activeTopics) {
                    // Prefetch data for the user's most recent topics (limit to 2)
                    const topicsToFetch = result.data.activeTopics.slice(0, 2);
                    topicsToFetch.forEach(topic => {
                        prefetchAllForTopic(topic);
                    });
                }
            } catch (err) {
                // Ignore errors
            }
        };

        // Delay prefetch to not block initial render
        const timer = setTimeout(prefetchUserTopics, 1000);
        return () => clearTimeout(timer);
    }, [currentUser, prefetchAllForTopic]);

    const value = {
        // Cached getters (instant if cached)
        getCachedLearningPath,
        getCachedWorldMap,
        getCachedBossBattle,
        getCachedGameContent,

        // Background prefetchers
        prefetchLearningPath,
        prefetchWorldMap,
        prefetchBossBattle,
        prefetchGameContent,
        prefetchAllForTopic,

        // Cache management
        invalidateCache,

        // Status
        prefetchStatus
    };

    return (
        <DataCacheContext.Provider value={value}>
            {children}
        </DataCacheContext.Provider>
    );
};
